// Lumière Hair Clinic — section brief 30 September 2026 (supersedes the 21 September hair brief where they differ).
// Not yet confirmed by the practice and therefore omitted: video assessments, the indicative cost range,
// technique beyond a generic description, the review schedule, commissioned line illustrations.
import { HAIRLINE_PLANNING } from "@/data/photos";

// Hair-section credential line: the practice asked for the surgical sub-specialty wording to be left off here.
export const HAIR_CREDENTIAL = "US fellowship-trained";

export const HAIR_SECTION_NAME = "Lumière Hair Clinic";
export const HAIR_BOOK_PATH = "/hair/assessment";

export const HAIR_POSITIONING =
  "The doctor who assesses you is the doctor who designs your hairline, performs your procedure, and sees you at every review.";

export const HAIR_PILLARS = [
  { title: "One doctor, start to finish", line: "No advisors, no handover, no rotating roster." },
  { title: "Diagnosis before treatment", line: "Thinning has several causes and they need different answers. A transplant is one option, not the default." },
  { title: "Trained in America, practising in Perth", line: "Advanced training in facial plastic and reconstructive surgery, including hair and brow restoration." },
  { title: "Here, not overseas", line: "Every review afterwards in person, a short drive away — not aftercare by phone from another country." },
  { title: "Honest about candidacy", line: "A proportion of enquiries are told not yet, or not you. We say so plainly." },
];

export const HAIR_SECTION_NAV = [
  { to: "/hair/hair-loss", label: "Hair loss" },
  { to: "/hair/treatment", label: "Treatments" },
  { to: "/hair/transplantation", label: "Transplantation" },
  { to: "/hair/dr-kristy-truong", label: "Dr Truong" },
  { to: "/hair#faq", label: "FAQ" },
  { to: "/hair#clinic", label: "The clinic" },
];

export const HAIR_LOSS_CARDS = [
  { slug: "male-pattern", title: "Male pattern hair loss", text: "The most common pattern and the most treatable — Norwood stages explained, and why the stage changes the answer." },
  { slug: "hairline-and-temples", title: "Hairline and temple recession", text: "The most design-sensitive area of all — planned, never improvised." },
  { slug: "crown-thinning", title: "Crown thinning", text: "The swirl at the back that consumes grafts quickly — planned for impression, not density." },
  { slug: "female-thinning", title: "Female hair thinning", text: "Often medical before it is cosmetic. Dr Kiran Shahid assesses first." },
  { slug: "eyebrows", title: "Eyebrow thinning", text: "Brow restoration uses finer grafts and different angles. Assessment first, as always." },
  { slug: "scalp-health", title: "Scalp health", text: "The foundation everything else sits on — examined before any treatment is chosen." },
].map((c) => ({ ...c, to: `/hair/hair-loss/${c.slug}` }));

export const HAIR_TREATMENT_CARDS = [
  { to: "/hair/treatment", title: "Assessment and medical treatment", text: "Diagnosis first, then prescription-strength stabilisation where it helps. Many patients need nothing further." },
  { to: "/hair/treatment/prp-and-prf", title: "PRP and PRF", text: "Your own blood, prepared and returned to the scalp to support the follicles you still have." },
  { to: "/hair/transplantation", title: "Transplantation", text: "For stable loss with good donor hair — planned and performed by Dr Kristy Truong." },
];

// Hair sub-menu (HairHeader) and the main-site Hair mega-menu share these columns.
export const HAIR_MENU = {
  hairLoss: {
    label: "Hair loss",
    to: "/hair/hair-loss",
    links: [...HAIR_LOSS_CARDS.map(({ to, title }) => ({ to, label: title })), { to: "/hair/hair-loss", label: "All hair loss, explained" }],
  },
  treatments: {
    label: "Treatments",
    to: "/hair/treatment",
    links: [
      ...HAIR_TREATMENT_CARDS.map(({ to, title }) => ({ to, label: title === "Transplantation" ? "Hair transplantation" : title })),
      { to: "/hair/transplantation/is-it-right-for-you", label: "Is a transplant right for you?" },
      { to: "/hair/transplantation/procedure-day", label: "Procedure day" },
      { to: "/hair/transplantation/recovery", label: "Recovery" },
      { to: "/hair/transplantation/cost", label: "Cost" },
    ],
  },
  practice: {
    label: "Lumière Hair Clinic",
    links: [
      { to: "/hair/dr-kristy-truong", label: "Dr Kristy Truong" },
      { to: "/hair#faq", label: "Questions" },
      { to: "/hair#clinic", label: "The clinic" },
      { to: "/hair/journal", label: "Journal" },
      { to: HAIR_BOOK_PATH, label: "Book an assessment" },
    ],
  },
};

// Main-site mega menu (Header.jsx)
export const HAIR_NAV = {
  understanding: HAIR_MENU.hairLoss.links,
  treatment: HAIR_MENU.treatments.links,
  practice: HAIR_MENU.practice.links,
};

export const HAIR_HERO = {
  room: HAIRLINE_PLANNING,
  truong: { id: "/images/hair/dr-kristy-truong-scrubs.webp", alt: "Dr Kristy Truong in Lumière Hair Clinic scrubs", pos: "80% 15%", mobilePos: "72% 25%" },
  roomDetail: {
    id: "/images/treatment-room.webp",
    alt: "A detail of the Lumière treatment room in soft daylight",
    pos: "50% 70%",
  },
  portrait: { id: "/images/hair/dr-kristy-truong-scrubs.webp", alt: "Dr Kristy Truong in Lumière Hair Clinic scrubs", pos: "72% 20%" },
};

export const HAIR_LANDING = {
  seoTitle: "Hair Loss Treatment Perth | Lumière Hair Clinic, Cottesloe",
  seoDescription: "Hair loss treatment in Perth, planned by a doctor. Diagnosis first, then medical treatment, PRP or transplantation — assessed, planned and performed by Dr Kristy Truong in Cottesloe.",
  eyebrow: "Lumière Hair Clinic · Cottesloe, Perth",
  h1: "US-trained hair transplant doctor, now in Perth.",
  standfirst: "Work with Dr Kristy Truong to choose the best options for you — diagnosis first, then medical treatment, PRP or transplantation, in person in Cottesloe.",
  bullets: [
    "Doctor-run — a physician assesses you first",
    "In person in Cottesloe, inside a medical centre",
    "45-minute assessment, $250, redeemable against treatment within 30 days",
  ],
  jumpNav: [
    { id: "treatments", label: "Your options" },
    { id: "why", label: "Why Lumière Hair Clinic" },
    { id: "doctor", label: "Dr Truong" },
    { id: "stages", label: "Where am I?" },
    { id: "how", label: "How it works" },
    { id: "clinic", label: "The clinic" },
    { id: "faq", label: "FAQ" },
    { id: "book", label: "Book" },
  ],
  noticed: {
    eyebrow: "Sound familiar?",
    heading: "Have you noticed changes in your hair?",
    items: [
      "More scalp in the mirror under bright light, or in photographs",
      "A parting that has widened, or a ponytail that feels thinner than it was",
      "Temples that have crept back, or a crown you have started checking",
      "Reaching for a hat more often than you used to",
      "Comparing today's reflection with how it looked a few years ago",
    ],
    body: "These changes are common, and they are worth taking seriously early — hair that is thinning is far easier to protect than hair that has gone. An assessment commits you to nothing. It tells you what is happening, why, and what would honestly help.",
  },
  options: {
    eyebrow: "Your options",
    heading: "Four ways forward — chosen with your doctor.",
    intro: "Not sure what's right for you? Start with the assessment. The right treatment depends on the cause and the stage — and it is not always a transplant.",
    cards: [
      { to: "/hair/treatment", title: "Medical treatment", text: "Diagnosis first, then prescription-strength stabilisation where it helps. Many patients need nothing further.", image: { id: "/images/hair/tiles/medical-treatment.webp", pos: "50% 50%" }, alt: "Lumière Hair Clinic prescription medicine bottles and boxes on linen" },
      { to: "/hair/treatment/prp-and-prf", title: "PRP and PRF", text: "Non-surgical. Your own blood, prepared and returned to the scalp to support the follicles you still have.", image: { id: "/images/hair/tiles/prp-prf.webp", pos: "50% 40%" }, alt: "Dr Kristy Truong in Lumière Hair Clinic scrubs holding a tube of prepared plasma" },
      { to: "/hair/hair-loss/scalp-health", title: "Scalp health", text: "The foundation everything else sits on — examined, and treated where it needs to be, before any treatment is chosen.", image: { id: "/images/hair/tiles/scalp-health.webp", pos: "50% 55%" }, alt: "Dr Kristy Truong examining a scalp with a dermatoscope" },
      { to: "/hair/transplantation", title: "Hair transplantation", text: "For stable loss with good donor hair — planned and performed by Dr Kristy Truong, in Perth.", image: { id: "/images/hair/tiles/transplantation.webp", pos: "50% 30%" }, alt: "Dr Kristy Truong in surgical loupes examining a hairline" },
    ],
  },
  stages: {
    eyebrow: "Where am I?",
    heading: "Where does your hair sit on the scale?",
    intro: "Doctors grade pattern loss on the Norwood scale for men and the Ludwig scale for women. The stage matters less as a label than for what it changes: which treatments still work, and whether a procedure is ever the right conversation. Tap the drawing that looks most like you to see the usual path from there.",
    menLabel: "Men — the Norwood scale",
    womenLabel: "Women — the Ludwig scale",
    menTo: "/hair/hair-loss/male-pattern",
    womenTo: "/hair/hair-loss/female-thinning",
  },
  why: {
    eyebrow: "Why Lumière Hair Clinic",
    heading: "How we practise hair restoration.",
  },
  // DRAFT — written for Dr Truong's approval; replace with her own words when supplied.
  quote: {
    text: "Hair loss is a medical condition before it is a cosmetic one. My job is to find the cause, tell you honestly what will and won't help, and — if a procedure is right — plan and perform it myself, and see you at every review afterwards.",
    name: "Dr Kristy Truong",
    title: "The Procedural Physician · AHPRA MED0002351485",
  },
  how: {
    eyebrow: "How it works",
    heading: "Six steps, in order.",
    intro: "The same sequence for everyone, whatever the pattern. Nothing permanent is planned until the cause is known and the loss is stable.",
  },
  clinic: {
    eyebrow: "The clinic",
    heading: "In Cottesloe, inside a medical centre.",
    body: "Lumière Hair Clinic is the hair restoration section of Lumière Aesthetics, a doctor-run practice on Marine Parade. The practice sits inside an established medical centre — so arriving for a hair assessment looks exactly like arriving for a GP appointment. Every consultation, procedure and review happens here, in person.",
    mapsUrl: "https://www.google.com/maps/search/?api=1&query=Shop+3%2F40+Marine+Parade%2C+Cottesloe+WA+6011",
  },
  faqs: [
    { q: "What causes hair loss in men and women?", a: "Most often genetics — male or female pattern loss. But hormonal change, iron and thyroid problems, stress and illness, medications, scalp conditions and post-partum shedding all thin hair, and each needs a different answer. That is why every programme here begins with a doctor's assessment rather than a product." },
    { q: "Which types of hair loss do you treat?", a: "Male and female pattern loss, hairline and temple recession, crown thinning, eyebrow thinning and scalp conditions. Where the cause is medical, it is treated as a medical problem first — within the medical centre, by a doctor." },
    { q: "Do I need a hair transplant?", a: "Not necessarily, and often not. Many patients — particularly those who come early — stabilise their loss with medical treatment and need nothing more. A transplant is one option for stable loss with good donor hair; your assessment tells you whether it applies to you." },
    { q: "How long before I notice a change?", a: "It varies with the treatment and the person. Medical treatment is typically judged over three to six months; a PRP course runs over several months; transplanted hair sheds first and reaches its final result at twelve to eighteen months. Your written plan sets out the timeline that applies to you." },
    { q: "Why do I need an assessment first?", a: "Because the right treatment depends entirely on the cause, and the cause cannot be guessed from a photograph. The assessment includes a history, scalp examination, dermatoscopy and blood tests where indicated — and ends with a diagnosis and a written plan you keep whether or not you act on it." },
    { q: "What does the assessment cost?", a: "A hair assessment with Dr Truong is $250 for 45 minutes, in person at Cottesloe, and redeemable against treatment within 30 days. A $100 deposit is taken at booking and credited to your consultation." },
    { q: "Do you treat women?", a: "Yes. Female hair thinning is more often medical in origin, so women are assessed first by Dr Kiran Shahid, whose background is skin-medicine diagnosis. Where a procedure is the right answer, Dr Truong plans and performs it." },
    { q: "Is it discreet?", a: "Yes. The practice sits inside a medical centre, consultations are private and unhurried, and clinical photographs are for your record only — never for marketing." },
  ],
  book: {
    eyebrow: "Book",
    heading: "Book a hair assessment.",
    body: "Forty-five minutes with the doctor who plans and performs every procedure herself. Choose a time below, or ask us to call you.",
    widgetHeading: "Choose a time with Dr Truong",
    includes: ["A proper history and scalp examination", "Dermatoscopy — magnified examination of the follicles", "Blood tests where indicated", "A diagnosis, and a written plan you keep"],
  },
  oneDoctor: [
    { title: "Assessment", text: "A physician examines your scalp, takes a proper history, and identifies the cause." },
    { title: "Planning", text: "Your hairline is designed by the same doctor, by hand, with you." },
    { title: "Procedure and after", text: "She performs it, and she is the doctor you see at every review." },
  ],
  candidacy: {
    heading: "Honest about candidacy",
    body: "We decline a proportion of the people who ask us about transplantation — because their loss is still progressing, because the donor supply won't support the goal, or because medical treatment is the better answer first. That conversation is the assessment.",
  },
};

export const HAIR_LOSS_HUB = {
  seoTitle: "Understanding Hair Loss | Lumière Hair Clinic, Cottesloe",
  seoDescription: "Male pattern loss, hairline recession, crown thinning, female thinning, eyebrows and scalp health — what is happening, what can honestly be done, and who to see at Lumière Hair Clinic, Cottesloe.",
  eyebrow: "Lumière Hair Clinic · Hair loss",
  h1: "Understanding hair loss.",
  standfirst: "Thinning has several causes, and they need different answers. Start with what's happening — the assessment confirms it.",
  sequence: {
    heading: "Diagnosis before treatment",
    intro: "Whatever the pattern, the order here is the same. Nothing permanent is planned until the cause is known and the loss is stable.",
    steps: [
      { title: "Diagnose", text: "History, examination, dermatoscopy and blood tests where indicated — because thyroid, iron and scalp conditions all thin hair, and none of them is treated with a transplant." },
      { title: "Stabilise", text: "Where loss is active, medical treatment first. Many patients — particularly those who come early — never need more." },
      { title: "Restore, where it's right", text: "PRP and PRF to support living follicles; transplantation for stable loss with good donor hair, planned and performed by Dr Truong." },
    ],
  },
};

// "Where am I?" quiz — what usually happens at each stage, and the usual path. Educational, not diagnostic.
const P = {
  assess: { label: "Assessment and medical treatment", to: "/hair/treatment" },
  prp: { label: "PRP and PRF", to: "/hair/treatment/prp-and-prf" },
  transplant: { label: "Hair transplantation", to: "/hair/transplantation" },
  right: { label: "Is a transplant right for you?", to: "/hair/transplantation/is-it-right-for-you" },
  cost: { label: "What a transplant costs", to: "/hair/transplantation/cost" },
  scalp: { label: "Scalp health", to: "/hair/hair-loss/scalp-health" },
  grows: { label: "Read: How hair grows", to: "/journal/how-hair-grows" },
  women: { label: "Read: Hair loss in women", to: "/journal/hair-loss-in-women" },
};

const NORWOOD_PATHS = [
  { usual: "Nothing to treat. If hair loss runs in your family, a baseline assessment and photographs give you something to compare against later — and that is all.", path: [P.assess, P.grows] },
  { usual: "A mature hairline, not necessarily hair loss. Treatment isn't usually needed. An assessment is worth it if the recession has changed over the past year, or if the crown has started to feel thinner.", path: [P.assess, P.scalp] },
  { usual: "The stage where medicine does the most. The usual path is a diagnosis, medical stabilisation, and a review at three to six months — nothing permanent is planned until the loss has stopped moving.", path: [P.assess, P.prp, P.right] },
  { usual: "Medical treatment to hold what remains, first. Once the loss is stable and the donor hair allows, transplant planning becomes a genuine conversation — with coverage, not density, as the honest goal.", path: [P.assess, P.right, P.transplant, P.cost] },
  { usual: "Medical treatment continues to protect the band that remains. Transplantation is planned around the donor supply, often in stages, and the plan is written down before anything is booked.", path: [P.right, P.transplant, P.cost] },
  { usual: "Donor supply, not ambition, sets what a transplant can achieve. Some people at this stage are candidates for a realistic, limited plan; many are not — and the assessment is where we tell you which.", path: [P.right, P.assess, P.transplant] },
  { usual: "Transplantation is rarely the right answer at this stage, and we say so. An assessment is still worthwhile: to talk honestly about what medicine and scalp care can and cannot do.", path: [P.assess, P.right, P.scalp] },
];

const LUDWIG_PATHS = [
  { usual: "Find the cause first — hormonal change, iron, thyroid, stress, medication. Treating it does the most at this grade, and many women who come early never need anything more.", path: [P.assess, P.women] },
  { usual: "Medical treatment protects what remains; regenerative scalp treatment supports follicles that are thinning but alive. Both begin with a doctor's diagnosis of the cause.", path: [P.assess, P.prp, P.women] },
  { usual: "Honest planning territory: what medicine can hold, what can be supported, and — rarely, and only after the cause is known — whether transplantation has a part to play.", path: [P.assess, P.prp, P.right] },
];


export const MALE_PATTERN_EXTRA = {
  norwood: {
    heading: "The Norwood stages, in plain language",
    intro: "Doctors grade male pattern loss on the Norwood scale, from I to VII. The stage matters less as a label than for what it changes: which treatments still work, how much donor hair a plan can spend, and whether the pattern has finished declaring itself.",
    stages: [
      { stage: "I", name: "No visible loss", text: "A juvenile or mature hairline with no recession. Nothing to treat." },
      { stage: "II", name: "Mature hairline", text: "Slight recession at the temples. Many men stay here for life; it is not, on its own, a reason to act." },
      { stage: "III", name: "First real recession", text: "Deeper temple recession, sometimes with early crown thinning (III vertex). The earliest stage at which treatment is usually worth discussing — and the stage where medicine does the most." },
      { stage: "IV", name: "Recession and crown", text: "Recession deepens and the crown thins, with a band of hair still separating the two areas. Medical treatment still matters; transplant planning becomes a genuine conversation when loss is stable." },
      { stage: "V", name: "The band narrows", text: "The bridge between front and crown thins. Coverage, rather than density, is the honest planning goal." },
      { stage: "VI", name: "Front and crown join", text: "The bridge is gone. Donor supply, not ambition, sets what a transplant can achieve." },
      { stage: "VII", name: "Advanced loss", text: "A narrow horseshoe of hair remains at the back and sides. Transplantation is rarely the right answer; we say so." },
    ].map((s, i) => ({ ...s, image: `/images/hair/norwood/stage-${i + 1}.webp`, alt: `Norwood stage ${s.stage} — ${s.name.toLowerCase()}: schematic line drawing of the scalp from above and in three-quarter view`, ...NORWOOD_PATHS[i] })),
    note: "Illustrations are schematic and show the typical pattern at each stage. Your own pattern, and how quickly it is changing, are assessed in person.",
  },
  whyStage: {
    heading: "Why the stage changes the answer",
    paras: [
      "At stage II or III, most follicles are miniaturising rather than gone — which is exactly the situation prescription treatment is good at. Started then, medicine preserves the hair you have for years, and a transplant may never be needed.",
      "By stage V or VI, the question is different: how much permanent donor hair exists at the back and sides, and how to spend it for the most impression. The answer is coverage and framing, planned honestly, not a return to a stage-I head of hair.",
    ],
  },
  pathways: {
    heading: "Three pathways, in sequence",
    items: [
      { title: "Medical treatment", text: "Prescription therapy to slow or stabilise active loss, prescribed and monitored by a doctor. Priced in consultation, as regulation requires." },
      { title: "PRP and PRF", text: "Your own platelets returned to the scalp to support follicles that are thinning but alive. A course, then maintenance." },
      { title: "Transplantation", text: "For stable loss with adequate donor supply — hairline designed by hand, performed by Dr Truong, reviewed by her in person." },
    ],
  },
  timing: {
    heading: "When to treat, and when to wait",
    paras: [
      "Treat early where loss is active — preserving hair is far easier than replacing it. Wait on anything permanent while the pattern is still declaring itself, which for many men means the twenties. Waiting is not doing nothing: medical treatment through those years protects the hair you have, and the transplant conversation, if it comes, is a better one.",
    ],
  },
};

export const FEMALE_THINNING_EXTRA = {
  ludwig: {
    eyebrow: "The Ludwig scale",
    heading: "The Ludwig grades, in plain language",
    intro: "Female pattern loss is graded differently from men's. The Ludwig scale describes what women actually see — a parting that widens over the crown while the front hairline stays put — in three grades. As with the Norwood scale, the grade matters for what it changes: how much medicine can still protect, and whether a procedure is ever the right conversation.",
    stages: [
      { stage: "I", name: "Early widening", text: "The parting is a little wider than it was; more scalp shows in bright light or a photograph. The front hairline is untouched. The stage where finding the cause — hormonal, iron, thyroid, stress — and treating it does the most, and where most women who come early stay." },
      { stage: "II", name: "Noticeable thinning", text: "The parting has clearly widened and the hair over the crown has lost volume — a smaller ponytail, harder to style over. Medical treatment still protects what remains; regenerative scalp treatment supports follicles that are thinning but alive." },
      { stage: "III", name: "Extensive thinning", text: "Most of the crown is see-through, with only a fringe of hair preserved at the front and full hair at the sides and back. Honest planning territory: what medicine can hold, what can be supported, and — rarely, and only after diagnosis — whether transplantation has a part to play." },
    ].map((s, i) => ({ ...s, image: `/images/hair/ludwig/grade-${i + 1}.webp`, alt: `Ludwig grade ${s.stage} — ${s.name.toLowerCase()}: schematic line drawing of a woman's scalp from above and in three-quarter view`, ...LUDWIG_PATHS[i] })),
    note: "Illustrations are schematic and show the typical pattern at each grade. Diffuse shedding from a medical cause can look like any of these while barely touching the scale — which is why the assessment always starts with a history and, where indicated, blood tests.",
  },
  whyGrade: {
    heading: "Why the grade changes the answer",
    paras: [
      "At grade I, the hair is thinning rather than gone. Follicles that are miniaturising respond to treatment of the cause and, where the pattern is genetic, to medical stabilisation — so the first job is a diagnosis, not a product. Many women who come at this stage never need anything more.",
      "By grade III, the question is different: how much of the crown can be supported, and whether the hair at the sides and back could ever be redistributed. Transplantation is less often the answer in women than in men, because the loss is diffuse rather than patterned and the donor area is often thinning too. Where it is right, Dr Truong plans it — but only after Dr Shahid has confirmed the cause.",
    ],
  },
  journal: { to: "/journal/hair-loss-in-women", label: "Read: Hair loss in women" },
};

export const TRUONG_HAIR_PROFILE = {
  seoTitle: "Dr Kristy Truong — Hair Restoration Doctor, Cottesloe | Lumière Hair Clinic",
  seoDescription: "Dr Kristy Truong assesses, plans and performs every hair restoration procedure at Lumière Hair Clinic in Cottesloe. US fellowship-trained in facial plastic and reconstructive surgery, with further training in hair and brow restoration.",
  eyebrow: "Lumière Hair Clinic · Your doctor",
  standfirst: "The doctor who assesses you designs your hairline, performs your procedure, and sees you at every review.",
  sections: [
    {
      heading: "Training",
      paras: [
        "Dr Kristy Truong is US fellowship-trained in facial plastic and reconstructive surgery, with training completed at Georgetown, Iowa and North Carolina, and further training in hair and brow restoration. She is registered with AHPRA as a medical practitioner (MED0002351485).",
        "That training matters for hair work in a specific way. A hairline is a facial feature: where it sits, how it curves, how it will age are judgements about a face, not a scalp — and here they are made by a doctor whose training is in the face.",
      ],
    },
    {
      heading: "What she does at Lumière",
      paras: [
        "Dr Truong leads the hair restoration programme from assessment and diagnosis through medical treatment planning, hairline design, the procedure itself and every review afterwards. She also leads the practice's most complex facial work — staged treatment plans, thread-based lifting, and revision of results from other clinics.",
        "Female hair thinning is assessed first by Dr Kiran Shahid, whose background is skin-medicine diagnosis, because the causes in women are more often medical. Where a procedure is the right answer, Dr Truong plans and performs it.",
      ],
    },
    {
      heading: "Her approach to hairline design",
      paras: [
        "A hairline is drawn by hand, with you in the chair and a mirror in front of you — not generated, not templated. It is designed for the face you will have at fifty, not the one you had at twenty: a conservative height, an irregular natural edge, temples that suit your bone structure, and a density plan that keeps donor hair in reserve.",
        "The drawing is agreed and recorded at planning, and it is the same drawing that is marked on procedure day. Nothing is improvised on the morning.",
      ],
    },
    {
      heading: "Every procedure, herself",
      paras: [
        "Dr Truong performs every hair restoration procedure at Lumière herself. There is no advisor between you and the doctor, and no handover between the person who planned your result and the person who produces it. Every review afterwards is in person, with her, in Cottesloe.",
      ],
    },
    {
      heading: "When the answer is no",
      paras: [
        "A proportion of the people who ask Dr Truong about transplantation are told not yet, or not at all — because loss is still progressing, because donor supply won't support the goal, or because medicine is the better first answer. She considers that the assessment working, not failing, and she will tell you plainly.",
      ],
    },
  ],
};

export const HAIR_BOOKING_PAGE = {
  path: "/hair/assessment",
  serviceKey: "hair",
  title: "Book a hair assessment with Dr Kristy Truong | Lumière Hair Clinic, Cottesloe",
  description: "Hair restoration begins with a diagnosis. A 45-minute assessment with Dr Kristy Truong, in person at Cottesloe — $250, redeemable against treatment within 30 days.",
  eyebrow: "Lumière Hair Clinic · Cottesloe",
  h1: "Hair restoration begins with a diagnosis.",
  standfirst: "Forty-five minutes with the doctor who plans and performs every procedure herself — in person at Cottesloe.",
  reasons: [
    { title: "One doctor, start to finish", text: "The doctor who assesses you designs your hairline, performs your procedure and sees you at every review." },
    { title: "Diagnosis before treatment", text: "Thinning has several causes. The assessment finds yours before anything is planned — and a transplant is one option, not the default." },
    { title: "Here, not overseas", text: "Every review in person in Cottesloe, a short drive away. Not aftercare by phone from another country." },
  ],
  offer: ["$250", "45 minutes", "Redeemable against treatment within 30 days"],
  widget: "hairInPerson",
  widgetHeading: "Choose a time with Dr Truong",
  credentials: "Dr Kristy Truong — The Procedural Physician · US fellowship-trained in facial plastic and reconstructive surgery (Georgetown, Iowa, North Carolina) · Further training in hair and brow restoration · AHPRA MED0002351485",
};

export const PRACTICE_STANDARDS = {
  title: "Our practice standards",
  body: "Every hair transplant patient at Lumière has an in-depth assessment, a review of the medical causes of their hair loss, and at least seven days between consenting and the procedure. We also ask for a letter from your GP. These are our standards rather than legal requirements — we set them because the procedure is permanent.",
};

export const PATHWAY = [
  { step: "Assessment", line: "A doctor examines your scalp, your history and the cause of your loss." },
  { step: "Medical review", line: "Active loss is stabilised before anything permanent is planned." },
  { step: "Hairline design and planning", line: "Your hairline is drawn, agreed and recorded." },
  { step: "Consent and interval", line: "Written consent, then at least seven days before the procedure — our standard." },
  { step: "Procedure day", line: "One day under local anaesthetic, home the same evening." },
  { step: "Recovery and reviews", line: "Scheduled reviews across the following twelve months." },
];

export const HAIR_LANDING_PANEL = {
  inBrief: [
    { label: "Time", value: "45-minute assessment to begin" },
    { label: "Downtime", value: "None for medical treatment or PRP" },
    { label: "Results last", value: "Transplanted hair is permanent; medical results while treatment continues" },
  ],
  covers: HAIR_TREATMENT_CARDS.map((t) => ({ label: t.title, to: t.to })),
  suits: [
    "Early to moderate thinning where follicles are still active",
    "Hair loss that has stabilised, or been stabilised with medical treatment",
  ],
};

export const HAIR_JOURNAL_SLUGS = ["understanding-the-norwood-scale", "how-hair-grows", "hair-loss-in-women"];

export const ASSESSMENT_PAGE = {
  slug: "treatment",
  title: "Assessment and medical treatment",
  eyebrow: "Lumière Hair Clinic · Treatment",
  standfirst: "Every hair programme begins with a doctor's assessment — because the right treatment depends entirely on the cause.",
  whatHappens: {
    heading: "What happens at a hair assessment",
    intro: "Forty-five minutes with a doctor, structured around an answer. A hair assessment at Lumière includes:",
    items: [
      "A proper history — when the loss began, how it is progressing, your health and medications",
      "Examination of the scalp and the pattern of loss",
      "Dermatoscopy — magnified examination of the follicles themselves",
      "Photographs for your clinical record — never for marketing",
      "Blood tests where indicated",
      "A diagnosis, and a written plan you keep whether or not you act on it",
    ],
    outro: "Suitability screening, including screening for body-dysmorphic concerns, is part of every assessment. It is how a medical practice should approach appearance medicine.",
  },
  medical: {
    heading: "Medical treatment",
    paras: [
      "For most patterns of loss, medicine comes before any procedure. Prescription treatments can slow or stabilise active loss, and other treatments support regrowth from follicles that are miniaturising but still alive. Your doctor will explain the categories plainly — what each can realistically do, what it asks of you, and what it costs — before anything is prescribed.",
    ],
    rxNote: "Prescription hair treatments are priced in consultation, as Australian advertising regulations require, with a written quote before anything is booked. No product is named on this website for the same reason.",
  },
  wholeAnswer: {
    heading: "When medical treatment is the whole answer",
    body: "Many patients — particularly those who come early — stabilise their loss with medical treatment and are satisfied without any procedure at all. We consider that a success, not a missed sale. If your assessment says medicine is enough, that is what your plan will say.",
  },
  whoToSee: {
    heading: "Who to see",
    note: "Dr Truong leads assessment and treatment planning for hair restoration. Dr Shahid assesses female hair loss, scalp conditions and any presentation where the cause may be medical — diagnosis before anything cosmetic.",
    practitioners: ["truong", "shahid"],
  },
  risks: [
    "All medical treatments carry the possibility of side effects — these are discussed plainly in consultation before anything is prescribed",
    "Not every treatment suits every patient; that is what the assessment is for",
    "Not all thinning responds to treatment — your doctor will tell you where that applies",
  ],
  cost: {
    heading: "Cost",
    body: "A hair assessment with Dr Truong is $250 (45 minutes, in person at Cottesloe), redeemable against treatment within 30 days. A $100 deposit is taken at booking and credited to your consultation. Ongoing medical treatment costs are set out in your written plan.",
  },
};

export const PRP_PAGE = {
  slug: "prp-and-prf",
  title: "PRP and PRF for the scalp",
  eyebrow: "Lumière Hair Clinic · Treatment",
  standfirst: "Your own blood, prepared and returned to the scalp — to support the follicles you still have.",
  whatItIs: [
    "PRP (platelet-rich plasma) and PRF (platelet-rich fibrin) begin with a small blood draw, taken here in the practice. The sample is prepared to concentrate the growth factors your blood already carries, then returned to the scalp through a series of fine injections.",
    "Neither treatment contains anything foreign — no drug, no donor material. The premise is simple: follicles that are miniaturising but alive can be supported to produce thicker, stronger hair.",
  ],
  suits: [
    "Early to moderate thinning where follicles are still active",
    "Men and women wanting to support medical treatment",
    "Patients maintaining results after a transplant",
    "Those preferring a treatment derived from their own blood",
  ],
  notSuits: [
    "Areas where follicles are long gone — PRP supports existing follicles, it does not create new ones",
    "Anyone with a blood disorder or on blood-thinning medication — assessed at consultation",
    "Anyone expecting a guaranteed response — biology varies, and we will tell you what is realistic for your scalp",
  ],
  course: {
    heading: "What a course involves",
    body: "A course is usually three sessions, four to six weeks apart, then maintenance once or twice a year. Each session takes around forty-five minutes, including preparation of your sample.",
  },
  canCannot: {
    heading: "What it can and cannot do",
    can: "Support existing, living follicles to produce thicker hair; slow visible thinning; improve scalp condition.",
    cannot: "Regrow hair from follicles that have gone. A smooth, bare area of scalp is a different conversation — and we will have it honestly.",
  },
  recovery: [
    "Tenderness and small raised bumps at injection points settle over a day or two",
    "Wash your hair the next morning, not the same day",
    "No downtime — most patients return to work the same day",
  ],
  risks: [
    "Bruising and tenderness at injection points",
    "Temporary headache or scalp tightness",
    "No guarantee of response — outcomes vary between individuals, and your assessment sets expectations honestly",
  ],
  practitioners: ["shahid", "truong"],
  pricingNote: "PRP and PRF are not prescription medicines, so their pricing is published: a single scalp session is $650 and a course of three $1,750, listed under Rejuran and regenerative on our pricing page. Your assessment confirms whether a course is worth your while before anything is booked.",
};

export const TRANSPLANT_HUB = {
  title: "Hair transplantation",
  eyebrow: "Lumière Hair Clinic · Transplantation",
  standfirst: "Permanent hair restoration, planned by a doctor, performed by Dr Kristy Truong.",
  inBrief: [
    ["Duration", "One day"],
    ["Anaesthetic", "Local"],
    ["Final result", "12–18 months"],
  ],
  whatItIs: [
    "A hair transplant moves living follicles from the part of your scalp that keeps its hair for life — usually the back and sides — to the area of loss. Because those follicles are genetically permanent, the result is permanent too: transplanted hair grows, can be cut, and behaves like the hair it always was.",
    "The technique — FUE, where follicles are taken individually, or FUT, where a narrow strip is taken and divided — is chosen at planning. Either way, the procedure is a single day under local anaesthetic, planned and performed by Dr Truong.",
  ],
  suits: [
    "Hair loss that has stabilised, or been stabilised with medical treatment",
    "Enough donor hair at the back and sides for the goal",
    "Realistic expectations — density is planned honestly, not promised",
    "Medical causes ruled out or treated first",
    "Generally over 25–30, unless loss has clearly stabilised",
  ],
  notSuits: [
    "Active, progressing loss without stabilisation — that comes first",
    "Limited donor supply for the result you want",
    "Certain scalp conditions, assessed at consultation",
    "Expectations a transplant cannot honestly meet",
    "Anyone Dr Truong assesses as unlikely to benefit",
  ],
  declineNote: "We decline a proportion of the people who ask. That is part of the assessment, not a failure of it.",
  panelBrief: [
    { label: "Time", value: "One day, under local anaesthetic" },
    { label: "Downtime", value: "Desk work within days; nothing strenuous for two weeks" },
    { label: "Results last", value: "Permanent — final result at 12–18 months" },
  ],
  costNote: "Your written quote follows assessment and includes everything listed on our cost page.",
  risks: [
    "Infection",
    "Bleeding",
    "Swelling",
    "Temporary numbness",
    "Temporary shedding of existing hair",
    "Scarring in the donor area — small dot scars with FUE, a linear scar with FUT",
    "Uneven, lower-than-expected or delayed growth",
    "The possibility of a further session",
    "Results vary between individuals",
  ],
  faqs: [
    { q: "Will it look natural?", a: "That is the entire design exercise. Hairline placement, graft angle and density are planned for your face and your age — the aim is hair that draws no comment at all." },
    { q: "How many grafts will I need?", a: "It depends on the area, your donor supply and the density planned. Your assessment ends with a number and a written quote — not an estimate pulled from a chart." },
    { q: "Will I need more than one procedure?", a: "Sometimes. Loss that continues, or a large area, can call for a second session — we plan for that possibility from the first conversation rather than discovering it later." },
    { q: "What if I'm not a candidate?", a: "You will be told, plainly, and shown what is worth doing instead — medical treatment, PRP, or sometimes simply time. A proportion of assessments end this way." },
  ],
  subPages: [
    { to: "/hair/transplantation/is-it-right-for-you", title: "Is it right for you?", text: "The most important page in this section — who transplantation suits, and who it honestly doesn't." },
    { to: "/hair/transplantation/procedure-day", title: "Procedure day", text: "Hour by hour: arrival, marking, anaesthetic, extraction, implantation, and going home." },
    { to: "/hair/transplantation/recovery", title: "Recovery, month by month", text: "From the first scabs to the final result at twelve to eighteen months — including the shedding phase nobody warns you about." },
    { to: "/hair/transplantation/cost", title: "Cost", text: "What determines the price, what's included, and how payment works." },
    { to: "/hair/transplantation/areas", title: "Areas", text: "Hairline, crown, brows, beard and the female hairline — what's different about each." },
    { to: "/hair/transplantation/faq", title: "Questions", text: "Everything patients ask before they commit — answered plainly." },
  ],
};

export const TRANSPLANT_SUBPAGES = [
  {
    slug: "is-it-right-for-you",
    title: "Is it right for you?",
    eyebrow: "Lumière Hair Clinic · Transplantation",
    standfirst: "The most important page in this section. Most disappointments with hair transplantation come from operating on the wrong head, at the wrong time.",
    seoTitle: "Is a Hair Transplant Right for You? | Lumière Aesthetics",
    blocks: [
      {
        type: "paras",
        heading: "The question we ask first: is your hair loss stable?",
        paras: [
          "This is the question everything else hangs on, and it is the one the industry least likes to ask. A transplant moves permanent follicles into an area of loss — but it does nothing to slow the loss itself. If your hair is still actively thinning, the native hair around the transplants continues to recede, and within a few years the result can look worse than no procedure at all: islands of transplanted hair in a sea of continued loss.",
          "Transplanting into active loss is how poor long-term results are made. It is also why every patient here begins with an assessment and, where loss is active, a period of medical stabilisation first. When your loss has been stable for a reasonable period — naturally or with treatment — the transplant conversation becomes a good one. Until then, it is premature, and we will say so.",
        ],
      },
      {
        type: "paras",
        heading: "Donor supply",
        paras: [
          "A transplant does not create hair; it relocates it. The permanent zone at the back and sides of your head is the only supply there is, and it is finite. 'Enough' donor hair means being able to take what your goal requires while leaving the donor area looking untouched — and keeping a reserve, because loss can continue over a lifetime.",
          "This is why two men with identical hairlines can receive different answers. The one with dense donor hair can plan generously; the one with fine or limited donor hair must plan conservatively, or accept a more modest goal. Your assessment includes an honest measurement of what your donor supply can achieve — before anyone talks about what it costs.",
        ],
      },
      {
        type: "paras",
        heading: "Age",
        paras: [
          "Younger men are often asked to wait, and it is worth understanding why. Hair loss in the early twenties is usually still declaring itself — the pattern it will eventually take is not yet visible. Transplanting a low, adolescent-style hairline at twenty-two commits a limited donor supply to a design that can become a liability at thirty-five.",
          "Waiting is not doing nothing. Medical treatment during those years preserves the hair you have, which is far easier than replacing it later — and when the pattern has declared itself, the transplant conversation is a much better one. If you are young and losing hair, the assessment is still worth having; it simply ends differently.",
        ],
      },
      {
        type: "paras",
        heading: "Expectations: density versus coverage",
        paras: [
          "A transplant can restore a hairline, rebuild a crown, and return framing to a face. What it cannot do is return the density of an eighteen-year-old head of hair — no technique can, because the follicles have to come from somewhere. The honest planning conversation is about coverage and impression: where hair makes the most difference, at a density that looks right, within what your donor supply allows.",
          "Patients who arrive expecting their teenage hair back leave disappointed no matter how good the work is. Patients who arrive wanting to look like themselves with hair leave pleased. Setting that expectation correctly is not underselling — it is the difference between a result you live with happily and one you chase.",
        ],
      },
      {
        type: "paras",
        heading: "Medical causes first",
        paras: [
          "Not all hair loss is pattern loss. Thyroid conditions, iron deficiency, autoimmune conditions, medication effects and post-partum change all thin hair, and each has a different correct response — most of them medical rather than procedural. This is why your assessment includes a history, examination and blood tests where indicated.",
          "For female patients especially, this step matters most. Female hair thinning is more often medical in origin, the pattern is different, and transplantation is less often the answer — which is why women are assessed by Dr Shahid, whose background is skin-medicine diagnosis, before any procedure is contemplated.",
        ],
      },
      {
        type: "paras",
        heading: "When we say no",
        paras: [
          "A proportion of the people who ask for a transplant here are declined. Active loss that hasn't been stabilised, donor supply that can't meet the goal, expectations the procedure can't honestly satisfy — each is a reason to say not yet, or not at all. We decline because the procedure is permanent, and a permanent result you regret is a genuine harm. If that is the answer your assessment produces, you will hear it plainly, with an honest account of what is worth doing instead.",
        ],
      },
    ],
    faqs: [
      { q: "Can I have a transplant while I'm still losing hair?", a: "Not here. Transplanting into active loss produces poor long-term results. We stabilise first, then plan — that sequence is not negotiable." },
      { q: "Am I too young for a transplant?", a: "Generally we ask patients under 25–30 to wait unless loss has clearly stabilised. The assessment is still worthwhile — medical treatment in those years preserves what you have." },
      { q: "How do I know if I have enough donor hair?", a: "That is measured at assessment, not guessed. Your donor density determines what can be achieved and is part of your written plan." },
      { q: "What can I do while I wait?", a: "Medical stabilisation, PRP where indicated, and reviews to track the pattern. Most patients who wait well end up needing less surgery, not more." },
      { q: "Does the assessment commit me to anything?", a: "No. You leave with a diagnosis and a written plan. What you do with it, and when, is entirely yours." },
    ],
  },
  {
    slug: "procedure-day",
    title: "Procedure day",
    eyebrow: "Lumière Hair Clinic · Transplantation",
    standfirst: "One unhurried day under local anaesthetic — from the morning's first photographs to the drive home.",
    seoTitle: "Hair Transplant Procedure Day | Lumière Aesthetics",
    blocks: [
      {
        type: "paras",
        heading: "How the day runs",
        paras: [
          "You arrive in the morning and we sit down first: the consent you signed at least seven days earlier is reviewed, any last questions are answered, and nothing proceeds until you are settled. Your hairline design is then confirmed and marked — the same drawing agreed at planning, checked against your face one final time. Photographs are taken for your clinical record.",
          "The local anaesthetic is the only genuinely uncomfortable part of the day, and it is brief: a series of small stings across the donor and recipient areas, over in minutes, after which you feel pressure and movement but not pain. Most patients are surprised by how unremarkable this part is.",
          "Extraction follows — follicles taken from the permanent zone at the back and sides, individually with FUE or as a strip with FUT, according to your plan. After lunch, implantation: each graft placed at the angle and direction your natural hair grows, which is where the artistry of the day lives. There are proper breaks throughout — for the bathroom, for lunch, to stretch. The procedure typically takes a full day, and you go home the same evening with written aftercare, an aftercare kit, and our number.",
        ],
      },
      {
        type: "list",
        heading: "Practical notes",
        items: [
          "Wear a button-up or zip-front shirt — nothing that pulls over your head",
          "Eat normally; lunch is broken for properly",
          "Take your usual medications unless we've told you otherwise at planning",
          "Arrange a lift home — you shouldn't drive after a full procedure day",
          "Bring someone if you'd like company for the morning; the procedure itself is private",
        ],
      },
    ],
    risks: true,
    faqs: [
      { q: "Does the day hurt?", a: "The local anaesthetic stings briefly at the start; after that you feel pressure, not pain. Most patients describe the day as long rather than uncomfortable." },
      { q: "Can I go home the same day?", a: "Yes — everyone does, with written aftercare and a direct number for anything that concerns you." },
      { q: "Will I be awake?", a: "Yes, throughout. Local anaesthetic only — you can talk, listen to something, or doze." },
    ],
  },
  {
    slug: "recovery",
    title: "Recovery, month by month",
    eyebrow: "Lumière Hair Clinic · Transplantation",
    standfirst: "The honest timeline — including the shedding phase that patients find hardest.",
    seoTitle: "Hair Transplant Recovery Timeline | Lumière Aesthetics",
    blocks: [
      {
        type: "timeline",
        items: [
          { when: "Days 1–3", what: "Swelling, tenderness and small scabs around each graft. Sleep slightly upright, follow the washing instructions exactly, and let the scabs be — they protect the grafts." },
          { when: "Days 4–10", what: "The scabs lift away on their own. Most patients return to desk work within days. No gym, no swimming, nothing that raises a sweat into the grafts." },
          { when: "Weeks 2–8", what: "The shedding phase: the transplanted hairs fall out. This is normal, expected, and the part patients find hardest — the follicles remain, alive, beneath the skin. It looks like nothing happened. It is supposed to." },
          { when: "Months 3–4", what: "New growth begins — fine and soft at first, like new hair always is." },
          { when: "Months 6–9", what: "Noticeable density. The hair thickens, darkens, and starts behaving like hair." },
          { when: "Months 12–18", what: "The final result. Growth continues to mature past the first year." },
        ],
      },
      {
        type: "paras",
        heading: "Reviews",
        paras: [
          "Your recovery is reviewed in person at day 3, week 2, month 3, month 6 and month 12. Between reviews, you can reach the practice directly — questions during recovery are normal and expected.",
        ],
      },
      {
        type: "callout",
        heading: "When to call us, same-day",
        body: "Increasing pain rather than settling pain; spreading redness, warmth or swelling; any discharge or fever; bleeding that doesn't stop with gentle pressure; or anything that simply feels wrong to you. Call the practice — do not wait for a scheduled review.",
      },
    ],
    faqs: [
      { q: "When can I go back to work?", a: "Desk work, typically within days. Physical work waits until the grafts are secure — usually around two weeks." },
      { q: "When can I exercise?", a: "Nothing that raises a sweat for the first ten days to two weeks, then a gradual return. Swimming waits a little longer." },
      { q: "Will people be able to tell?", a: "For the first week, yes, up close — small scabs are visible. A loose hat is fine after the first few days, and most patients time the procedure around a quiet fortnight." },
    ],
  },
  {
    slug: "cost",
    title: "Cost",
    eyebrow: "Lumière Hair Clinic · Transplantation",
    standfirst: "A written quote before anything is booked — and everything it includes, in writing.",
    seoTitle: "Hair Transplant Cost Cottesloe | Lumière Aesthetics",
    blocks: [
      {
        type: "paras",
        heading: "What determines cost",
        paras: [
          "Four things: the number of grafts, the area being treated, the technique, and whether more than one session is needed. Each is settled at assessment and planning, which is why an honest price can only follow an assessment — not precede one.",
        ],
      },
      {
        type: "paras",
        heading: "The indicative range",
        paras: [
          "Your written quote follows your assessment and is fixed — it does not change on the day.",
        ],
      },
      {
        type: "list",
        heading: "What's included",
        items: [
          "Your assessment and planning",
          "The procedure itself, planned and performed by Dr Truong",
          "Medications around the procedure",
          "Your aftercare kit",
          "All scheduled reviews for twelve months",
        ],
      },
      {
        type: "list",
        heading: "What's not included",
        items: [
          "Ongoing medical treatment for hair loss, where prescribed",
          "Optional PRP or PRF sessions",
        ],
      },
      {
        type: "paras",
        heading: "Payment",
        paras: [
          "A deposit is taken after the consent interval, with the balance before procedure day. We do not offer finance or payment plans, we do not discount, and we will never encourage borrowing or accessing superannuation for an elective procedure. If the cost is a stretch, the honest advice is to wait — the procedure is permanent, and so is debt.",
          "Transplant planning begins with a hair assessment — 45 minutes, $250, redeemable against treatment within 30 days. A $100 deposit is taken at booking and credited to your consultation.",
        ],
      },
    ],
    faqs: [
      { q: "Why can't I get a price before assessment?", a: "Because the graft count, area and technique aren't known until your scalp is examined. A price before that is a guess dressed as a quote." },
      { q: "Is the consultation fee extra?", a: "It's redeemable — the assessment fee comes off treatment within 30 days." },
    ],
  },
  {
    slug: "areas",
    title: "Areas",
    eyebrow: "Lumière Hair Clinic · Transplantation",
    standfirst: "Hairline, crown, brows, beard and the female hairline — what's different about each.",
    seoTitle: "Hair Transplant Areas | Lumière Aesthetics",
    blocks: [
      {
        type: "areas",
        items: [
          { title: "Hairline and temples", body: "The most design-sensitive work in the field — the frame of the face, seen every day. Hairline design is done slowly, agreed with you, and drawn to suit your age rather than your twenties. Usually a moderate procedure; recovery is typical of any recipient area." },
          { title: "Crown", body: "The crown consumes grafts quickly because of its surface area and the swirl pattern it grows in. Planning here is about impression rather than density, and donor supply is budgeted carefully. Often a larger procedure." },
          { title: "Eyebrows", body: "Brow restoration uses finer grafts placed at flatter angles than scalp work — brow hair grows almost parallel to the skin. A smaller, meticulous procedure, planned by Dr Truong." },
          { title: "Beard", body: "Beard work fills patches and sharpens density using scalp donor hair. The angles differ from the scalp, and the donor choice is matched to beard texture." },
          { title: "Female hairline", body: "Female hair loss is more often medical in origin, and transplantation is less often the answer — which is why women see Dr Shahid first for diagnosis. Where a transplant is right, hairline and part-line work is planned with Dr Truong." },
        ],
      },
    ],
    faqs: [
      { q: "Which area gives the most change?", a: "The hairline, almost always — it frames the face. It's also the area that most punishes poor design, which is why we plan it slowly." },
      { q: "Can more than one area be done in a day?", a: "Sometimes, where graft numbers allow. Your plan will say either way." },
    ],
  },
  {
    slug: "faq",
    title: "Questions",
    eyebrow: "Lumière Hair Clinic · Transplantation",
    standfirst: "Everything patients ask before they commit — answered plainly.",
    seoTitle: "Hair Transplant FAQ | Lumière Aesthetics",
    blocks: [],
    faqs: [
      { q: "Does it hurt?", a: "The local anaesthetic stings briefly at the start of the day; after that you feel pressure and movement, not pain. Tenderness afterwards is managed with simple measures and settles over days." },
      { q: "Will it look natural?", a: "That is the design exercise — hairline placement, graft angle and density planned for your face and age. Done properly, the result draws no comment at all." },
      { q: "How many grafts do I need?", a: "It depends on the area, your donor supply and the density planned. Your assessment ends with a number and a written quote, not an estimate from a chart." },
      { q: "Will I need more than one procedure?", a: "Sometimes. Continued loss or a large area can call for a second session — we plan for that possibility from the first conversation." },
      { q: "Can I have it done if I'm still losing hair?", a: "Not here. Transplanting into active loss produces poor long-term results. We stabilise first, then plan." },
      { q: "What about the back of my head — will it show?", a: "FUE leaves small dot scars, invisible once hair grows over them at normal length; FUT leaves a fine linear scar concealed by surrounding hair. Your plan covers which applies to you." },
      { q: "When can I go back to work?", a: "Desk work, typically within days. Physical work waits around two weeks." },
      { q: "When can I exercise?", a: "Nothing that raises a sweat for ten days to two weeks, then a gradual return. Swimming waits a little longer." },
      { q: "Will the transplanted hair fall out?", a: "The hairs shed in weeks 2–8 — normal and expected — then regrow from month 3–4. The follicles themselves are permanent." },
      { q: "Do I still need medical treatment afterwards?", a: "Often, yes. A transplant restores the treated area; medical treatment protects the hair you still have around it. Your plan covers both." },
      { q: "Can women have hair transplants?", a: "Yes, where it's the right answer — but female hair loss is more often medical, so women are assessed by Dr Shahid first. Diagnosis before any procedure." },
      { q: "Do you do eyebrows or beards?", a: "Yes — both use finer grafts and different angles than scalp work, and both begin with the same assessment." },
      { q: "What if I'm not a candidate?", a: "You'll be told plainly, with an honest account of what is worth doing instead — medical treatment, PRP, or time. A proportion of assessments end this way, and that's the system working." },
    ],
  },
];

export const getTransplantSubPage = (slug) => TRANSPLANT_SUBPAGES.find((p) => p.slug === slug);

const HOME = { name: "Home", to: "/" };
const HAIR = { name: "Hair", to: "/hair" };
export const HAIR_BREADCRUMBS = {
  landing: [HOME, HAIR],
  child: (name, to) => [HOME, HAIR, { name, to }],
  hairLoss: (name, to) => [HOME, HAIR, { name: "Hair loss", to: "/hair/hair-loss" }, ...(name ? [{ name, to }] : [])],
  treatment: (name, to) => [HOME, HAIR, { name: "Treatment", to: "/hair/treatment" }, ...(name ? [{ name, to }] : [])],
  transplant: (name, to) => [HOME, HAIR, { name: "Transplantation", to: "/hair/transplantation" }, ...(name ? [{ name, to }] : [])],
};
